/**
 * Small shared nav state that lives outside React: the recently-visited routes the
 * ⌘K palette lists, and the bus the sidebar's search button uses to open it.
 */

const RECENT_KEY = "komoran-recent-routes";
const MAX_RECENT = 5;

export function readRecentRoutes(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((r) => typeof r === "string") : [];
  } catch {
    return [];
  }
}

export function rememberRoute(to: string) {
  const next = [to, ...readRecentRoutes().filter((r) => r !== to)].slice(0, MAX_RECENT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // private mode / quota — recents are a nicety, never a hard failure
  }
}

const openListeners = new Set<() => void>();

export function onOpenPalette(cb: () => void) {
  openListeners.add(cb);
  return () => openListeners.delete(cb);
}

export function openCommandPalette() {
  openListeners.forEach((cb) => cb());
}

/**
 * Rute na kojima je barkod skener primarni način unosa. Na njima je ⌘K s
 * tastature potpuno isključen: skener i tastatura dijele isti kanal, pa nema
 * pouzdanog načina da se razlikuju. Dugme za pretragu u sidebaru i dalje radi.
 */
const SCANNER_ROUTES: RegExp[] = [
  /^\/warehouse\/brojanje-robe\/[^/]+\/count\/?$/,
  /^\/warehouse\/brojanje-posiljki\/[^/]+\/count\/?$/,
  /^\/warehouse\/scan\/?$/,
  /^\/warehouse\/move\/?$/,
  /^\/fulfillment\/pick\/?$/,
  /^\/fulfillment\/pack\/?$/,
];

export function isScannerRoute(pathname: string): boolean {
  return SCANNER_ROUTES.some((re) => re.test(pathname));
}

/**
 * Da li keydown treba otvoriti ⌘K paletu.
 *
 * Namjerno strogo: barkod skeneri (posebno ugrađeni na Android PDA uređajima)
 * kucaju kao tastatura i znaju poslati meta-state uz znakove — a na bs/hr
 * QWERTZ rasporedu AltGr = Ctrl+Alt, pa je "ctrl + k" lako pogoditi slučajno.
 * Paleta se onda otvori usred skeniranja i sljedeći Enter iz skenera odabere
 * prvu stavku iz "Nedavno" — radnik završi na Pripremi umjesto na brojanju.
 *
 * Zato: iz bilo kojeg polja za unos prečica nikad ne radi (ni ⌘, ni Ctrl), a na
 * skenerskim rutama ne radi uopće.
 */
export function isPaletteShortcut(e: KeyboardEvent, pathname?: string): boolean {
  if (e.repeat) return false;
  if (e.key?.toLowerCase() !== "k") return false;
  if (!(e.metaKey || e.ctrlKey)) return false;
  // AltGr javlja ctrl+alt, a Shift nije dio prečice — oboje znači "ovo nije ⌘K".
  if (e.altKey || e.shiftKey) return false;
  if (isEditableTarget(e.target)) return false;
  if (pathname !== undefined && isScannerRoute(pathname)) return false;
  return true;
}

/**
 * Da li Enter u paleti smije potvrditi označenu stavku.
 *
 * cmdk automatski označi prvu stavku čim se paleta otvori, pa bi prvi Enter
 * (npr. sufiks iz skenera) odveo na prvu stavku iz "Nedavno". Enter zato radi
 * tek kad je korisnik nešto otkucao ili se strelicama pomjerio po listi.
 */
export function paletteEnterAllowed(opts: { query: string; armed: boolean }): boolean {
  return opts.armed || opts.query.trim().length > 0;
}

function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  if (el.isContentEditable) return true;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
}
